import React, { useContext, useEffect, useState } from 'react';
import Draggable from 'react-draggable';
import { create } from 'zustand';

import { useABLincoln } from '@vimeo/ablincoln';
import {
  Badge,
  Button,
  Flex,
  IconButton,
  Portal,
  Tooltip,
  TooltipProps,
  useColorMode,
} from '@vimeo/bokeh';
import {
  BrightnessHalf,
  BrightnessHalfFilled,
  CloseX,
  CommentNotes,
  DragV,
  FillPaintBucket,
  PopOut_iris,
} from '@vimeo/bokeh-icons';
import { bokehTheme } from '@vimeo/bokeh-theme';
import { translate } from '@vimeo/i18n-runtime';
import { parseLocationSearch } from '@vimeo/query-feature-flag';
import { useViewer } from '@vimeo/viewer';

import { useCapability } from '../hooks/use-capability';
import { useIrisCustomSx } from '../hooks/use-iris-custom-sx';
import { useIsBokeh } from '../hooks/use-is-bokeh';
import useLocalStorage from '../hooks/use-storage/useLocalStorage';
import { RenderOnlyOnLivePages } from './RenderOnlyOnLivePages';
import { StaffOnly } from './StaffOnly';
import { ThemeDispatchContext } from './ThemeProvider';

const BokehTooltip = ({ children, ...props }: React.PropsWithChildren<TooltipProps>) => {
  const sx = useIrisCustomSx({
    ...bokehTheme.components.Tooltip.baseStyle,
    color: 'white',
    _dark: {
      color: 'white',
    },
  });
  return (
    <Tooltip {...props} sx={sx}>
      {children}
    </Tooltip>
  );
};

const Container = ({ children }: React.PropsWithChildren) => {
  return (
    <Portal>
      <Draggable bounds="body">
        <Flex
          bgColor="gray.800"
          _dark={{
            bgColor: 'white',
          }}
          borderWidth="1px"
          borderColor="stroke"
          pos="fixed"
          borderRadius="md"
          p="xs"
          gap="sm"
          w="max-content"
          align="center"
          left={{ base: '0', sm: 'calc(50% + 50px)' }}
          bottom={{ base: '50px', sm: 'lg' }}
          sx={{
            button: {
              _dark: {
                borderColor: 'darkBlueAlpha.300',
              },
            },
          }}
        >
          {children}
        </Flex>
      </Draggable>
    </Portal>
  );
};

const DragButton = () => {
  const sx = useIrisCustomSx({
    ...bokehTheme.components.Button?.variants?.primary,
  });
  return (
    <IconButton
      _hover={{ cursor: 'grab' }}
      _active={{ cursor: 'grabbing' }}
      aria-label="Drag toolbar"
      icon={<DragV />}
      variant="primary"
      size="sm"
      sx={sx}
    />
  );
};

const CloseButton = ({ onClick }: { onClick: VoidFunction }) => {
  const sx = useIrisCustomSx({
    ...bokehTheme.components.Button?.variants?.primary,
  });
  return (
    <BokehTooltip label="Hide the theme switcher for this session">
      <IconButton
        aria-label="Close switcher"
        icon={<CloseX />}
        variant="primary"
        size="sm"
        sx={sx}
        onClick={onClick}
      />
    </BokehTooltip>
  );
};

const FeedbackButton = () => {
  const sx = useIrisCustomSx({
    ...bokehTheme.components.Button?.variants?.primary,
  });

  return (
    <BokehTooltip label="Feedback">
      <IconButton
        as="a"
        target="_blank"
        href="https://forms.gle/uUJ2529T71yGhLbd9"
        size="sm"
        variant="primary"
        icon={<CommentNotes boxSize="20px !important" />}
        aria-label="Feedback"
        sx={sx}
      />
    </BokehTooltip>
  );
};

interface ThemeState {
  /**
   * Have we sycned with the ABLincoln experiment bucketing?
   * @default false
   */
  initialized: boolean;
  /**
   * Is the bokeh theme enabled?
   * @default true
   */
  hasBokeh: boolean;
  /**
   * Toggle the theme between bokeh and iris.
   */
  toggleTheme: VoidFunction;
  /**
   * Set the bokeh theme.
   */
  setBokehTheme: (hasBokeh: boolean) => void;
}

const useThemeStore = create<ThemeState>((set) => ({
  initialized: false,
  hasBokeh: true,
  toggleTheme: () => set((state) => ({ hasBokeh: !state.hasBokeh })),
  setBokehTheme: (hasBokeh: boolean) => set({ hasBokeh, initialized: true }),
}));

export const useGetUserBokehPreferences = () => {
  const getAbTest = useABLincoln();
  const viewer = useViewer();
  const hasBokeh = useThemeStore((state) => state.hasBokeh);
  const setBokehTheme = useThemeStore((state) => state.setBokehTheme);
  const initialized = useThemeStore((state) => state.initialized);

  const { capabilities } = useCapability(
    ['hasBokehTheme', 'canViewBokehTheme', 'canViewBokehThemeSwitcher'],
    viewer?.teamUser?.ownerId ?? viewer?.user?.id,
  );

  useEffect(() => {
    // Skip over logged-out users from being bucketed
    if (!viewer?.user) return;

    // Skip over users who shouldn't see the bokeh theme (ie. enterprise accounts)
    if (!capabilities.canViewBokehTheme) return;

    const bucket = getAbTest('JS.Bokeh.Rollout', 'bokeh_enabled', 'control', {
      cuid: viewer.user.id,
    });

    if (!initialized) {
      setBokehTheme(bucket === 'variant');
    }
    // setBokehTheme from `zustand` is stable on re-renders
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capabilities.canViewBokehTheme, getAbTest, viewer?.user, initialized]);

  return {
    hasBokeh,
  };
};

const BetaBadge = () => {
  const { hasBokeh } = useGetUserBokehPreferences();
  const label = hasBokeh ? 'You are previewing a feature not available to customers.' : '';
  const sx = useIrisCustomSx({
    ...bokehTheme.components.Badge?.variants?.new,
  });
  return (
    <BokehTooltip placement="top" label={label}>
      <Badge variant="new" size="sm" pos="absolute" right="-20px" top="-16px" sx={sx}>
        Beta
      </Badge>
    </BokehTooltip>
  );
};

const BokehThemeSwitcher = ({
  children,
  withIcon = true,
}: React.PropsWithChildren<{ withIcon?: boolean }>) => {
  const toggle = useThemeStore((state) => state.toggleTheme);

  const sx = useIrisCustomSx({
    ...bokehTheme.components.Button?.variants?.primary,
  });

  return (
    <BokehTooltip label="Change theme">
      <Button
        aria-label="Toggle theme"
        onClick={() => toggle()}
        leftIcon={withIcon ? <FillPaintBucket /> : undefined}
        variant="secondary"
        color="text-button-inverted"
        size="sm"
        _hover={{
          bgColor: 'lightBlueAlpha.300',
          _dark: {
            bgColor: 'darkBlueAlpha.400',
          },
        }}
        sx={sx}
      >
        {children}
      </Button>
    </BokehTooltip>
  );
};

const GoToStorybook = () => {
  const sx = useIrisCustomSx({
    ...bokehTheme.components.Button?.variants?.primary,
  });
  const isBokeh = useIsBokeh();
  const storybook = isBokeh
    ? 'https://bokeh.vimeows.com/?path=/docs/getting-started-intro--docs&globals=bokehColorMode:bokeh-light'
    : 'https://bokeh.vimeows.com/?path=/docs/getting-started-intro--docs';
  return (
    <BokehTooltip label="Visit Storybook">
      <IconButton
        variant="primary"
        size="sm"
        as="a"
        target="_blank"
        href={storybook}
        icon={<PopOut_iris boxSize="2xs!important" />}
        aria-label="Visit Storybook"
        sx={sx}
      />
    </BokehTooltip>
  );
};

type Theme = 'light' | 'dark';

const LightDarkSwitcher = () => {
  const { toggleTheme: toggleIrisTheme } = useContext(ThemeDispatchContext);
  const { setColorMode: toggleBokehTheme, colorMode } = useColorMode();

  const sx = useIrisCustomSx({
    ...bokehTheme.components.Button?.variants?.primary,
  });

  const handleThemeChange = (theme: Theme) => {
    toggleIrisTheme(theme);
    toggleBokehTheme(theme);
  };

  const mode = colorMode === 'dark' ? 'Dark mode' : 'Light mode';

  return (
    <BokehTooltip label="Change mode">
      <Button
        aria-label={mode}
        onClick={() => handleThemeChange(colorMode === 'dark' ? 'light' : 'dark')}
        variant="secondary"
        color="text-button-inverted"
        leftIcon={colorMode === 'dark' ? <BrightnessHalfFilled /> : <BrightnessHalf />}
        size="sm"
        _hover={{
          bgColor: 'lightBlueAlpha.300',
          _dark: {
            bgColor: 'darkBlueAlpha.400',
          },
        }}
        sx={sx}
      >
        {mode}
      </Button>
    </BokehTooltip>
  );
};

export const AdminOwnerBokehThemeToggle = () => {
  const [isOpen, setIsOpen] = useState(true);
  const { hasBokeh } = useGetUserBokehPreferences();

  if (!isOpen) return null;

  return (
    <Container>
      <DragButton />
      <BokehThemeSwitcher withIcon={false}>
        {hasBokeh ? translate('New Vimeo') : translate('Classic Vimeo')}
      </BokehThemeSwitcher>
      <LightDarkSwitcher />
      <CloseButton onClick={() => setIsOpen(false)} />
    </Container>
  );
};

export const BokehThemeToggle = () => {
  const [isOpen, setIsOpen] = useState(true);
  const { hasBokeh } = useGetUserBokehPreferences();

  if (!isOpen) return null;

  return (
    <Container>
      <DragButton />
      <BokehThemeSwitcher>{hasBokeh ? 'Bokeh' : 'Iris'}</BokehThemeSwitcher>
      <LightDarkSwitcher />
      <FeedbackButton />
      <GoToStorybook />
      <BetaBadge />
      <CloseButton onClick={() => setIsOpen(false)} />
    </Container>
  );
};

export const AdminOwnerOnly = ({ children }: React.PropsWithChildren) => {
  const viewer = useViewer();

  const { capabilities, ready } = useCapability(
    ['canViewBokehThemeSwitcher'],
    viewer?.teamUser?.ownerId ?? viewer?.user?.id,
  );

  if (!viewer?.user || !ready) return null;

  if (viewer?.user?.badge?.type === 'staff') return null;

  if (!capabilities?.canViewBokehThemeSwitcher) return null;

  return <>{children}</>;
};

export const BokehFeatureFlag = ({ children }: React.PropsWithChildren) => {
  const [show, setBokehFlag] = useLocalStorage('bokeh', null);
  const bokehParam = parseLocationSearch('bokeh');

  useEffect(() => {
    if (bokehParam !== null) {
      setBokehFlag(bokehParam);
    }
  }, [setBokehFlag, bokehParam]);

  // Hide the toolbar when users explicitly set ?bokeh=false, otherwise show the toolbar
  if (show === 'false') return null;

  return <>{children}</>;
};

export const BokehThemeToggles = () => {
  useGetUserBokehPreferences();

  return (
    <>
      <BokehFeatureFlag>
        <StaffOnly>
          <BokehThemeToggle />
        </StaffOnly>
      </BokehFeatureFlag>
      <RenderOnlyOnLivePages>
        <AdminOwnerOnly>
          <AdminOwnerBokehThemeToggle />
        </AdminOwnerOnly>
      </RenderOnlyOnLivePages>
    </>
  );
};
